import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Dev-only fallback login so the app isn't blocked end-to-end if the Okta
 * flow is unavailable. It never attaches a bearer token, so Okta-protected
 * managerium calls still fail for users who log in this way. In production
 * builds it is switched off, and the credentials below are tree-shaken out.
 */
export const LOCAL_AUTH_ENABLED = import.meta.env.DEV

const VALID_EMAIL = 'admin@n26.com'
const VALID_PASSWORD = '12345!'

type LocalAuthContextValue = {
  enabled: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => boolean
  logout: () => void
}

const disabled: LocalAuthContextValue = {
  enabled: false,
  isAuthenticated: false,
  login: () => false,
  logout: () => undefined,
}

const LocalAuthContext = createContext<LocalAuthContextValue>(disabled)

type LocalAuthProviderProps = {
  children: ReactNode
}

const EnabledLocalAuthProvider = ({ children }: LocalAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const value = useMemo<LocalAuthContextValue>(
    () => ({
      enabled: true,
      isAuthenticated,
      login: (email, password) => {
        const success = email === VALID_EMAIL && password === VALID_PASSWORD
        if (success) setIsAuthenticated(true)
        return success
      },
      logout: () => setIsAuthenticated(false),
    }),
    [isAuthenticated]
  )

  return (
    <LocalAuthContext.Provider value={value}>
      {children}
    </LocalAuthContext.Provider>
  )
}

export const LocalAuthProvider = LOCAL_AUTH_ENABLED
  ? EnabledLocalAuthProvider
  : ({ children }: LocalAuthProviderProps) => <>{children}</>

export const useLocalAuth = (): LocalAuthContextValue =>
  useContext(LocalAuthContext)
